import React, { useEffect } from "react";
import L from "leaflet";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { useTranslation } from "react-i18next";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [18.7893, 98.9746];

function LocationEvents({ onChange }) {
  useMapEvents({
    click(e) {
      onChange({
        lat: Number(e.latlng.lat.toFixed(7)),
        lng: Number(e.latlng.lng.toFixed(7)),
      });
    },
  });

  return null;
}

function RecenterMap({ position }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView([position.lat, position.lng], Math.max(map.getZoom(), 16));
    }
  }, [map, position]);

  return null;
}

export default function BuildingLocationPicker({ value, onChange, showLabel = true }) {
  const position =
    Number.isFinite(Number(value?.lat)) && Number.isFinite(Number(value?.lng))
      ? { lat: Number(value.lat), lng: Number(value.lng) }
      : null;

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition((pos) => {
      onChange({
        lat: Number(pos.coords.latitude.toFixed(7)),
        lng: Number(pos.coords.longitude.toFixed(7)),
      });
    });
  };

  const { t } = useTranslation();

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          {showLabel && (
            <label className="text-sm block m-1 font-bold text-slate-800">
              {t("admin.addBuilding.buildingLocationLabel")}
            </label>
          )}
          <div className="text-xs text-slate-400 ml-1">
            {t("admin.addBuilding.clickMapToPin")}
          </div>
        </div>
        <button
          type="button"
          onClick={useCurrentLocation}
          className="shrink-0 px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-blue-600 hover:bg-blue-50"
        >
          {t("admin.addBuilding.useCurrentLocation")}
        </button>
      </div>

      <div className="h-[280px] overflow-hidden rounded-xl border border-slate-200">
        <MapContainer
          center={position ? [position.lat, position.lng] : DEFAULT_CENTER}
          zoom={16}
          scrollWheelZoom
          className="h-full w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LocationEvents onChange={onChange} />
          <RecenterMap position={position} />
          {position && <Marker position={[position.lat, position.lng]} />}
        </MapContainer>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-500">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
          Lat: <span className="font-bold text-slate-700">{position?.lat ?? "-"}</span>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          Lng: <span className="font-bold text-slate-700">{position?.lng ?? "-"}</span>
        </div>
      </div>
    </div>
  );
}
